import React from 'react'
import { MetricCard } from '../shared/MetricCard'
import { useGameplayContext } from '../../contexts/gameplayContext'
import { formatCurrency, isDemoContentReached, isDemo, isInIframe, getEnergyIncome } from '../../utils'
import ActivitySelector from '../shared/ActivitySelector'
import { QUESTS } from '@/constants/quests'
import { Quest } from '../shared/Quest'
import CURRENCIES from '@/constants/currencies'
import { getIncomeBreakdown } from '@/engine/equipment'
import { IncomeBreakdown } from '../shared/IncomeBreakdown'
import { SurfLevelCard } from '../shared/SurfLevelCard'
import { Link } from 'react-router-dom'
import { MiniEquipmentCard } from '../shared/MiniEquipmentCard'

export const DashboardScreen: React.FC = () => {
  const { gameData } = useGameplayContext()
  const { jobIncome, hourlyFees, netIncome, feeBreakdown } = getIncomeBreakdown(gameData)
  const energyRate = getEnergyIncome(gameData)

  const activeQuest = gameData.activeMainQuest ? QUESTS[gameData.activeMainQuest] : null

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="col-span-2">
          <ActivitySelector />
        </div>
        <div className="col-span-2">
          <MiniEquipmentCard />
        </div>
        <div className="col-span-2">
          <SurfLevelCard skillPoints={gameData.currencies.skill.amount} />
        </div>
        <div className="col-span-2 xs:col-span-2 sm:col-span-1">
          <MetricCard
            emoji={CURRENCIES.money.icon}
            label={CURRENCIES.money.name}
            value={
              <div className="flex items-center">
                <div>${formatCurrency(gameData.currencies.money.amount)}</div>
                <div className={`text-xs ml-2 ${netIncome < 0 ? 'text-red-500' : 'text-green-700'}`}>
                  {netIncome >= 0 ? '+' : '-'}${formatCurrency(Math.abs(netIncome))}/hr
                </div>
              </div>
            }
            tooltip={
              <IncomeBreakdown
                jobIncome={jobIncome}
                hourlyFees={hourlyFees}
                netIncome={netIncome}
                feeBreakdown={feeBreakdown}
              />
            }
          />
        </div>
        <div className="col-span-2 xs:col-span-2 sm:col-span-1">
          <MetricCard
            emoji={CURRENCIES.energy.icon}
            label={CURRENCIES.energy.name}
            value={
              <div className="flex items-center">
                <div className="text-2xl">
                  {formatCurrency(gameData.currencies.energy.amount)}
                  <span className="text-xs"> / {formatCurrency(gameData.currencies.energy.max || 0)}</span>
                </div>
                {energyRate !== 0 && (
                  <div className={`text-xs ml-2 ${energyRate < 0 ? 'text-red-500' : 'text-green-700'}`}>
                    {energyRate >= 0 ? '+' : '-'}
                    {Math.abs(energyRate).toFixed(1)}/hr
                  </div>
                )}
              </div>
            }
          />
        </div>
      </div>
      <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 shadow-lg">
        <h3 className="text-xl font-semibold mb-2 text-white">Next Goal 🎯</h3>
        {isDemoContentReached(gameData) && isDemo() ? (
          isInIframe() ? (
            <div className="text-white space-y-3">
              <p className="text-yellow-300 font-semibold">You've reached the end of the demo content!</p>
              <p>
                To continue your surfing journey and unlock the next chapter, you need to activate the full game on{' '}
                <a
                  href="https://goatsurfer.goaat.xyz/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded transition-colors w-fit mb-3"
                >
                  <img src="/assets/the_goat_surfer_sm.png" alt="GOAT Surfer Logo" className="w-10 h-10" />
                  goatsurfer.goaat.xyz
                </a>
              </p>
            </div>
          ) : (
            <div className="text-white space-y-3">
              <p className="text-yellow-300 font-semibold">You've reached the end of the demo content!</p>
              <p>To continue your surfing journey and unlock the next chapter, you need to activate the full game.</p>
              <div className="flex mt-2">
                <Link
                  to="/settings"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2 px-4 rounded transition-colors inline-flex items-center"
                >
                  Activate in Settings
                </Link>
              </div>
            </div>
          )
        ) : activeQuest ? (
          <Quest quest={activeQuest} />
        ) : (
          <div className="text-white space-y-2">
            <p>All quests completed! Join our Discord to connect with other surfers and share your progress.</p>
            <div>
              <a
                href="https://discord.gg/VzEeEycYHa"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white hover:bg-gray-100 py-1.5 px-3 rounded transition-colors inline-flex items-center justify-center"
              >
                <img src="/assets/discord.svg" alt="Join Discord" className="h-6" />
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
